// lib/plans.ts
// Centralized plan and feature flag configuration

/**
 * Plus plan configuration - single source of truth for plan name and pricing
 * This is the canonical export for PLAN_PLUS across the entire codebase.
 */
export const PLAN_PLUS = {
  planName: 'Plus',
  monthlyPrice: 24.99,
  yearlyPrice: 224.99,
  yearlySavingsLabel: 'Save $74.89',
} as const;

export type PlanId = 'free' | 'basic' | 'pro' | 'enterprise';
export type Role = 'investor' | 'wholesaler';

export type FeatureKey =
  | 'saveFavorites'
  | 'alerts'
  | 'basicAnalytics'
  | 'advancedAnalytics'
  | 'heatmaps'
  | 'aiAnalyzer'
  | 'wholesalerPublish';

export const featuresByRoleAndPlan: Record<Role, Record<PlanId, FeatureKey[]>> = {
  investor: {
    free: [],
    basic: ['saveFavorites', 'alerts', 'aiAnalyzer'],
    pro: ['saveFavorites', 'alerts', 'aiAnalyzer', 'advancedAnalytics', 'heatmaps'],
    enterprise: ['saveFavorites', 'alerts', 'aiAnalyzer', 'advancedAnalytics', 'heatmaps'],
  },
  wholesaler: {
    free: [], // can still submit deals, but no publish feature
    basic: ['basicAnalytics', 'aiAnalyzer', 'wholesalerPublish'],
    pro: ['basicAnalytics', 'advancedAnalytics', 'heatmaps', 'aiAnalyzer', 'wholesalerPublish'],
    enterprise: ['basicAnalytics', 'advancedAnalytics', 'heatmaps', 'aiAnalyzer', 'wholesalerPublish'],
  },
};

/**
 * Check if a user with the given role and plan has access to a specific feature
 */
export function hasFeature(role: Role | null | undefined, plan: PlanId | null | undefined, feature: FeatureKey): boolean {
  if (!role || !plan) return false;
  const rolePlans = featuresByRoleAndPlan[role];
  if (!rolePlans) return false;
  return rolePlans[plan]?.includes(feature) ?? false;
}

/**
 * Get monthly AI analysis limit for a plan
 */
export function getMonthlyAnalysisLimit(plan: PlanId): number | 'unlimited' {
  if (plan === 'free') return 2;
  if (plan === 'basic') return 10;
  if (plan === 'pro' || plan === 'enterprise') return 'unlimited';
  return 0;
}
